import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { useTheme } from "vuetify";

interface NotificationItem {
  id: string;
  type: "success" | "error" | "warning" | "info";
  title: string;
  message: string;
  persistent?: boolean;
  timestamp: Date;
}

interface BreadcrumbItem {
  title: string;
  href?: string;
  disabled?: boolean;
}

export const useAppStore = defineStore("app", () => {
  // Theme management
  const theme = useTheme();

  // State
  const isLoading = ref(false);
  const loadingMessage = ref("Loading...");
  const sidebarOpen = ref(typeof window !== "undefined" ? window.innerWidth >= 1024 : true);
  const notifications = ref<NotificationItem[]>([]);
  const breadcrumbs = ref<BreadcrumbItem[]>([]);
  const isDarkMode = ref(localStorage.getItem("dark_mode") === "true");

  // App metadata
  const appInfo = ref({
    name: "DerraCare",
    version: "1.0.0",
    description: "Professional Medical Consultation Platform",
    supportEmail: "support@askmydoctor.com",
    docsUrl: "https://docs.askmydoctor.com",
  });

  // Getters
  const unreadNotifications = computed(() =>
    notifications.value.filter((n) => !n.persistent),
  );

  const notificationCount = computed(() => unreadNotifications.value.length);

  // Actions
  const setLoading = (loading: boolean, message = "Loading...") => {
    isLoading.value = loading;
    loadingMessage.value = message;
  };

  const toggleSidebar = () => {
    sidebarOpen.value = !sidebarOpen.value;
  };

  const setSidebar = (open: boolean) => {
    sidebarOpen.value = open;
  };

  const toggleTheme = () => {
    isDarkMode.value = !isDarkMode.value;
    theme.global.name.value = isDarkMode.value ? "dark" : "light";
    localStorage.setItem("dark_mode", isDarkMode.value.toString());
  };

  const setTheme = (dark: boolean) => {
    isDarkMode.value = dark;
    theme.global.name.value = dark ? "dark" : "light";
    localStorage.setItem("dark_mode", dark.toString());
  };

  const addNotification = (
    notification: Omit<NotificationItem, "id" | "timestamp">,
  ) => {
    const newNotification: NotificationItem = {
      ...notification,
      id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
      timestamp: new Date(),
    };

    notifications.value.unshift(newNotification);

    // Auto-remove non-persistent notifications after 5 seconds
    if (!notification.persistent) {
      setTimeout(() => {
        removeNotification(newNotification.id);
      }, 5000);
    }

    return newNotification.id;
  };

  const removeNotification = (id: string) => {
    const index = notifications.value.findIndex((n) => n.id === id);
    if (index > -1) {
      notifications.value.splice(index, 1);
    }
  };

  const clearNotifications = () => {
    notifications.value = [];
  };

  const setBreadcrumbs = (crumbs: BreadcrumbItem[]) => {
    breadcrumbs.value = crumbs;
  };

  const addBreadcrumb = (crumb: BreadcrumbItem) => {
    breadcrumbs.value.push(crumb);
  };

  const clearBreadcrumbs = () => {
    breadcrumbs.value = [];
  };

  // Utility functions
  const showSuccess = (title: string, message?: string) => {
    addNotification({
      type: "success",
      title,
      message: message || "",
    });
  };

  const showError = (title: string, message?: string) => {
    addNotification({
      type: "error",
      title,
      message: message || "",
      persistent: true,
    });
  };

  const showWarning = (title: string, message?: string) => {
    addNotification({
      type: "warning",
      title,
      message: message || "",
    });
  };

  const showInfo = (title: string, message?: string) => {
    addNotification({
      type: "info",
      title,
      message: message || "",
    });
  };

  // Device and viewport detection
  const isMobile = computed(() => {
    if (typeof window !== "undefined") {
      return window.innerWidth < 1024; // Align with CSS breakpoint
    }
    return false;
  });

  const isTablet = computed(() => {
    if (typeof window !== "undefined") {
      return window.innerWidth >= 768 && window.innerWidth < 1024;
    }
    return false;
  });

  const isDesktop = computed(() => {
    if (typeof window !== "undefined") {
      return window.innerWidth >= 1024;
    }
    return false;
  });

  // Initialize app
  const initialize = async () => {
    setLoading(true, "Initializing application...");

    try {
      // Set theme based on stored preference
      theme.global.name.value = isDarkMode.value ? "dark" : "light";

      // Auto-adjust sidebar based on device
      if (isMobile.value) {
        setSidebar(false);
      }

      // Add welcome notification
      showInfo(
        "Welcome to DerraCare",
        "Professional medical consultation platform",
      );
    } catch (error) {
      console.error("App initialization error:", error);
      showError("Initialization Error", "Failed to initialize the application");
    } finally {
      setLoading(false);
    }
  };

  // Window event listeners
  if (typeof window !== "undefined") {
    window.addEventListener("resize", () => {
      // Auto-hide sidebar on mobile
      if (isMobile.value && sidebarOpen.value) {
        setSidebar(false);
      }
    });

    // Handle online/offline status
    window.addEventListener("online", () => {
      showSuccess("Connection Restored", "You are now back online");
    });

    window.addEventListener("offline", () => {
      showWarning("Connection Lost", "You are currently offline");
    });
  }

  return {
    // State
    isLoading,
    loadingMessage,
    sidebarOpen,
    notifications,
    breadcrumbs,
    isDarkMode,
    appInfo,

    // Getters
    unreadNotifications,
    notificationCount,
    isMobile,
    isTablet,
    isDesktop,

    // Actions
    setLoading,
    toggleSidebar,
    setSidebar,
    toggleTheme,
    setTheme,
    addNotification,
    removeNotification,
    clearNotifications,
    setBreadcrumbs,
    addBreadcrumb,
    clearBreadcrumbs,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    initialize,
  };
});
